/**
 * 助手设置（/assistant/settings，与 iOS AssistantSettingsView 一致，去掉朗读相关）：
 * 家庭共用的百炼 / DeepSeek Key 和自定义服务（存服务端）、家长端助手的服务 / 模型 / 思考（存浏览器）、
 * 每个孩子平板上的小助手（children.settings.assistant）。
 */
import { ChevronLeft } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { confirm, DEFAULT_ASSISTANT_NAME, run, toast, useChildren, writeEntity, type Child, type ChildAssistantSettings } from "../../core";
import { Button, ChoiceChip, FormCard, IconButton, Page, PageTitle, RowDivider, SectionLabel, Switch, TextField, ValueRow } from "../../ui";
import { clearConversation } from "./agent";
import { streamChat } from "./chat";
import { DAILY_LIMITS, resolveVoice, supportsThinking, THINKING_LEVELS, VOICES, type Option } from "./providers";
import {
  chatRoute,
  deleteKey,
  fetchKeyStatuses,
  modelOptions,
  parseModelList,
  providerOptions,
  refreshCredentials,
  saveCustom,
  saveKey,
  setPrefs,
  useCreds,
  usePrefs,
  type KeyStatus,
} from "./settings";
import "./assistant.css";

export function SettingsPage() {
  const navigate = useNavigate();
  const children = useChildren();
  const prefs = usePrefs();
  const creds = useCreds();
  const [statuses, setStatuses] = useState<Record<string, KeyStatus> | null>(null);
  const [keyInput, setKeyInput] = useState("");
  const [deepseekInput, setDeepseekInput] = useState("");
  const [custom, setCustom] = useState({ name: "", url: "", key: "", models: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  const reload = async () => {
    try {
      const s = await fetchKeyStatuses();
      setStatuses(s);
      return s;
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), { error: true });
      return null;
    }
  };

  useEffect(() => {
    void refreshCredentials();
    void reload().then((s) => {
      // 读到自定义服务的配置后填进输入框
      const c = s?.custom;
      if (c?.configured) setCustom({ name: c.name ?? "", url: c.baseUrl ?? "", key: "", models: (c.models ?? []).join(", ") });
    });
  }, []);

  const dash = statuses?.dashscope;
  const deepseek = statuses?.deepseek;
  const customStatus = statuses?.custom;
  const route = chatRoute(prefs, creds);
  const customModels = parseModelList(custom.models);
  const customValid =
    !!custom.name.trim() && custom.url.trim().toLowerCase().startsWith("https://") && customModels.length > 0 && (!!custom.key.trim() || !!customStatus?.configured);

  const act = async (op: () => Promise<void>, done: string) => {
    setSaving(true);
    const ok = await run(async () => {
      await op();
      return true;
    }, done);
    await reload();
    setSaving(false);
    return !!ok;
  };

  const remove = async (provider: string, label: string) => {
    if (!(await confirm({ title: `清除${label}？`, message: "家长端和孩子平板上的小助手都会用不了这个服务。", confirmText: "清除", danger: true }))) return;
    if (await act(() => deleteKey(provider), "已清除")) {
      if (provider === "custom") setCustom({ name: "", url: "", key: "", models: "" });
      if (provider === "dashscope") setTestResult(null);
    }
  };

  /** 测试家长端当前选的对话服务和模型 */
  const test = async () => {
    const c = await refreshCredentials();
    const r = chatRoute(prefs, c);
    if (!r.key || r.customMissing) {
      setTestResult({ ok: false, text: `还没有设置${r.customMissing ? "自定义服务" : r.provider.name}的 API Key` });
      return;
    }
    setTesting(true);
    try {
      const turn = await streamChat({ provider: r.provider, apiKey: r.key, model: r.model, thinking: "off", messages: [{ role: "user", content: "用五个字以内回复：连接正常" }] });
      setTestResult({ ok: true, text: `可以使用（${r.provider.name} · ${r.model}：${turn.content.trim()}）` });
    } catch (e) {
      setTestResult({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setTesting(false);
    }
  };

  const clearChat = async (child?: Child) => {
    const title = child ? `清空${child.name}的对话记录？` : "清空家长端的对话记录？";
    if (!(await confirm({ title, message: "聊天记录和摘要都会删除，不能恢复。", confirmText: "清空", danger: true }))) return;
    await run(() => clearConversation(child?.id), "已清空");
  };

  const providers = providerOptions(customStatus);
  const parentModels = modelOptions(prefs.provider, true, customStatus);

  return (
    <Page gap={12}>
      <div className="asst-settings-top">
        <IconButton label="返回" onClick={() => (history.length > 1 ? navigate(-1) : navigate("/assistant"))}>
          <ChevronLeft size={22} strokeWidth={2.6} />
        </IconButton>
        <PageTitle>助手设置</PageTitle>
      </div>

      <SectionLabel>阿里云百炼 API Key（必需，家庭共用）</SectionLabel>
      <FormCard>
        <div className="asst-form">
          <KeyStatusText status={dash} loaded={!!statuses} />
          <TextField type="password" placeholder="sk-…" value={keyInput} onChange={setKeyInput} autoComplete="off" />
          <div className="asst-actions">
            <Button
              variant="accentPill"
              height={38}
              busy={saving}
              disabled={!keyInput.trim()}
              onClick={async () => {
                if (await act(() => saveKey("dashscope", keyInput.trim()), "已保存")) {
                  setKeyInput("");
                  setTestResult(null);
                }
              }}
            >
              保存
            </Button>
            {dash?.configured && (
              <button type="button" className="asst-link is-danger" onClick={() => void remove("dashscope", "百炼 API Key")}>
                清除
              </button>
            )}
            <span className="asst-spacer" />
            <button type="button" className="asst-link" disabled={!creds || testing} onClick={() => void test()}>
              {testing ? "正在测试…" : "测试"}
            </button>
          </div>
          {testResult && <p className={`asst-test ${testResult.ok ? "is-ok" : "is-bad"}`}>{testResult.text}</p>}
          <p className="asst-note">
            在百炼控制台（北京地域）创建。保存在家庭服务器上（加密），家长端和孩子平板上的小助手共用；平板只有在开启小助手后才能取到。对话也可以改用下面的 DeepSeek
            或自定义服务。模型都由浏览器、手机、平板直接调用，不经过家庭服务器。
          </p>
        </div>
      </FormCard>

      <SectionLabel>其它对话服务（可选，家庭共用）</SectionLabel>
      <FormCard>
        <div className="asst-form">
          <h3>DeepSeek API Key</h3>
          <KeyStatusText status={deepseek} loaded={!!statuses} />
          <TextField type="password" placeholder="sk-…" value={deepseekInput} onChange={setDeepseekInput} autoComplete="off" />
          <div className="asst-actions">
            <Button
              variant="accentPill"
              height={38}
              busy={saving}
              disabled={!deepseekInput.trim()}
              onClick={async () => {
                if (await act(() => saveKey("deepseek", deepseekInput.trim()), "已保存")) setDeepseekInput("");
              }}
            >
              保存
            </Button>
            {deepseek?.configured && (
              <button type="button" className="asst-link is-danger" onClick={() => void remove("deepseek", "DeepSeek API Key")}>
                清除
              </button>
            )}
          </div>
          <p className="asst-note">在 platform.deepseek.com 创建，对话按 DeepSeek 的价格计费。DeepSeek 不能看图，发图片时这一轮仍用百炼的看图模型。</p>
        </div>
        <RowDivider />
        <div className="asst-form">
          <h3>自定义服务（OpenAI 兼容接口）</h3>
          <KeyStatusText status={customStatus} loaded={!!statuses} />
          <TextField placeholder="名称，例如：公司模型" value={custom.name} onChange={(v) => setCustom({ ...custom, name: v })} />
          <TextField placeholder="地址，例如：https://api.example.com/v1" value={custom.url} onChange={(v) => setCustom({ ...custom, url: v })} />
          <TextField
            type="password"
            autoComplete="off"
            placeholder={customStatus?.configured ? "API Key（不改可留空）" : "API Key"}
            value={custom.key}
            onChange={(v) => setCustom({ ...custom, key: v })}
          />
          <TextField placeholder="模型，多个用逗号分开" value={custom.models} onChange={(v) => setCustom({ ...custom, models: v })} />
          <div className="asst-actions">
            <Button
              variant="accentPill"
              height={38}
              busy={saving}
              disabled={!customValid}
              onClick={async () => {
                const key = custom.key.trim();
                const ok = await act(() => saveCustom({ name: custom.name.trim(), baseUrl: custom.url.trim(), ...(key ? { apiKey: key } : {}), models: customModels }), "已保存");
                if (ok) setCustom({ ...custom, key: "" });
              }}
            >
              保存
            </Button>
            {customStatus?.configured && (
              <button type="button" className="asst-link is-danger" onClick={() => void remove("custom", "自定义服务")}>
                清除
              </button>
            )}
          </div>
          <p className="asst-note">地址填到 /v1 即可（会自动补上 /chat/completions），必须是 https，并且允许浏览器跨域访问。不会发送思考参数；服务返回了思考内容照样显示。</p>
        </div>
      </FormCard>

      <SectionLabel>家长端助手（只影响这个浏览器）</SectionLabel>
      <FormCard>
        <ValueRow title="对话服务" height={50}>
          <OptionSelect
            options={providers}
            value={prefs.provider}
            onChange={(p) => {
              // 换服务时模型不属于新服务就改用它的默认模型
              const list = modelOptions(p, true, customStatus);
              setPrefs({ provider: p, model: list.some((m) => m.id === prefs.model) ? prefs.model : (list[0]?.id ?? prefs.model) });
            }}
          />
        </ValueRow>
        <RowDivider />
        <ValueRow title="对话模型" height={50}>
          <OptionSelect options={parentModels} value={parentModels.some((m) => m.id === prefs.model) ? prefs.model : (parentModels[0]?.id ?? "")} onChange={(m) => setPrefs({ model: m })} />
        </ValueRow>
        <RowDivider />
        <ValueRow title="思考" height={50}>
          {prefs.provider === "custom" ? (
            <span className="asst-muted">自定义服务不设置</span>
          ) : supportsThinking(route.provider, route.model) ? (
            <OptionSelect options={THINKING_LEVELS} value={prefs.thinking} onChange={(t) => setPrefs({ thinking: t })} />
          ) : (
            <span className="asst-muted">该模型不支持</span>
          )}
        </ValueRow>
        <RowDivider />
        <button type="button" className="asst-danger-row" onClick={() => void clearChat()}>
          清空家长端的对话记录
        </button>
      </FormCard>
      <p className="asst-note">
        开启思考后回答更周全，但会慢一些；思考过程显示在对话里。发图片时会自动改用能看图的 VL 模型。对话记录一直保存在服务器上（手机和网页共用），很长时会在后台自动压缩成摘要，助手需要时会搜索以前的记录。
      </p>

      <SectionLabel>孩子平板上的小助手</SectionLabel>
      {children.length === 0 && <p className="asst-muted">还没有添加孩子。</p>}
      {children.map((c) => (
        <ChildCard key={c.id} child={c} customStatus={customStatus} onClear={() => void clearChat(c)} />
      ))}
      <p className="asst-note">开启后，孩子平板右下角会出现一只小鸟（名字可以自己起）：点一下说话、长按看对话，可以跳转页面、查时间表和学习任务、打开任务开始做；不能修改数据，也不陪聊。</p>
    </Page>
  );
}

/** 小助手名字的推荐 */
const NAME_PRESETS = ["小蛋糕", "小糖果"];

function ChildCard({ child, customStatus, onClear }: { child: Child; customStatus: KeyStatus | undefined; onClear: () => void }) {
  const cfg = child.settings?.assistant ?? {};
  const enabled = cfg.enabled === true;
  const provider = cfg.provider ?? "dashscope";
  const models = modelOptions(provider, false, customStatus);
  const model = cfg.model && models.some((m) => m.id === cfg.model) ? cfg.model : (models[0]?.id ?? "");
  const limit = cfg.dailyLimit ?? 50;

  /** 只合并传入的字段 */
  const update = (patch: ChildAssistantSettings) => void run(() => writeEntity("children", "PATCH", `children/${child.id}`, { settings: { assistant: patch } }));
  const name = cfg.name?.trim() || DEFAULT_ASSISTANT_NAME;
  const [nameDraft, setNameDraft] = useState(name);
  useEffect(() => setNameDraft(name), [name]);
  const saveName = (v: string) => {
    const n = v.trim().slice(0, 10);
    if ((n || DEFAULT_ASSISTANT_NAME) !== name) update({ name: n });
  };

  return (
    <FormCard>
      <Switch label={<b>{child.name}</b>} checked={enabled} onChange={(v) => update({ enabled: v })} />
      {enabled && (
        <>
          <RowDivider />
          <ValueRow title="名字">
            <div className="asst-name-row">
              {NAME_PRESETS.map((n) => (
                <ChoiceChip key={n} selected={name === n} onClick={() => saveName(n)}>
                  {n}
                </ChoiceChip>
              ))}
              <TextField
                className="asst-name-input"
                value={nameDraft}
                maxLength={10}
                placeholder="自己起名"
                aria-label="小助手的名字"
                onChange={setNameDraft}
                onBlur={() => saveName(nameDraft)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                }}
              />
            </div>
          </ValueRow>
          <RowDivider />
          <ValueRow title="对话服务">
            <OptionSelect options={providerOptions(customStatus)} value={provider} onChange={(p) => update({ provider: p })} />
          </ValueRow>
          <RowDivider />
          <ValueRow title="对话模型">
            <OptionSelect options={models} value={model} onChange={(m) => update({ model: m })} />
          </ValueRow>
          <RowDivider />
          <ValueRow title="朗读音色">
            <OptionSelect options={VOICES} value={resolveVoice(cfg.voice)} onChange={(v) => update({ voice: v })} />
          </ValueRow>
          <RowDivider />
          <ValueRow title="每天最多">
            <OptionSelect
              options={DAILY_LIMITS.map(([n, label]) => ({ id: String(n), name: label }))}
              value={String(DAILY_LIMITS.some(([n]) => n === limit) ? limit : 50)}
              onChange={(v) => update({ dailyLimit: Number(v) })}
            />
          </ValueRow>
          <RowDivider />
          <button type="button" className="asst-danger-row" onClick={onClear}>
            清空{child.name}的对话记录
          </button>
        </>
      )}
    </FormCard>
  );
}

function KeyStatusText({ status, loaded }: { status: KeyStatus | undefined; loaded: boolean }): ReactNode {
  if (!loaded) return <p className="asst-key-status">正在读取…</p>;
  if (!status?.configured) return <p className="asst-key-status">未设置</p>;
  const text = status.name ? `已设置：${status.name}（${(status.models ?? []).join("、")}）` : `已设置（${status.masked ?? "****"}）`;
  return <p className="asst-key-status is-on">{text}</p>;
}

function OptionSelect({ options, value, onChange }: { options: Option[]; value: string; onChange: (v: string) => void }) {
  const known = options.some((o) => o.id === value);
  return (
    <select className="asst-select" value={value} onChange={(e) => onChange(e.target.value)}>
      {!known && <option value={value}>{value || "—"}</option>}
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  );
}
